import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listTranslationRequests, updateTranslationRequest, getTranslationRequest } from '../../services/translationRequests';
import { uploadFileAndGetUrl } from '../../services/coa';
import { apiFetch } from '../../services/api';
import toast from 'react-hot-toast';
import DataTable from '../ui/DataTable';
import ActionMenu from '../ui/ActionMenu';
import {
  TRANSLATION_TYPE_OPTIONS,
  normalizeTranslationType,
  translationTypeBadgeClass
} from '../../constants/translationRequestType';
import {
  TRANSLATION_REQUEST_STATUS_OPTIONS,
  normalizeTranslationRequestStatus,
  translationRequestStatusBadgeClass
} from '../../constants/translationRequestStatus';

const TranslationRequestsTab = ({ licenceId, isEditable = true, onAwaitingCountChange, onTotalCountChange }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [translationRequests, setTranslationRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit form state
  const [status, setStatus] = useState('Under Review');
  const [translationFile, setTranslationFile] = useState(null);
  const [methodologyFile, setMethodologyFile] = useState(null);
  const [price, setPrice] = useState(0);
  const [translationStatus, setTranslationStatus] = useState('In Development – IHO');
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (licenceId) {
      loadTranslationRequests();
    }
  }, [licenceId]);

  const loadTranslationRequests = async () => {
    try {
      setLoading(true);
      const response = await listTranslationRequests({
        licenceId: licenceId,
        page: 1,
        limit: 100
      });

      if (response && response.success !== false) {
        const data = response.data || response;
        const list = data.translationRequests || data || [];
        setTranslationRequests(list);
        const awaitingCount = list.filter((r) => r.status === 'Pending').length;
        if (typeof onAwaitingCountChange === 'function') {
          onAwaitingCountChange(awaitingCount);
        }
        if (typeof onTotalCountChange === 'function') {
          onTotalCountChange(list.length);
        }
      }
    } catch (err) {
      console.error('Failed to load translation requests:', err);
      toast.error('Failed to load translation requests');
      setTranslationRequests([]);
      if (typeof onAwaitingCountChange === 'function') onAwaitingCountChange(0);
      if (typeof onTotalCountChange === 'function') onTotalCountChange(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (requestId) => {
    try {
      const response = await getTranslationRequest(requestId);
      if (response && response.success !== false) {
        const request = response.data?.translationRequest || response.data || response;
        setSelectedRequest(request);
        setStatus(normalizeTranslationRequestStatus(request.status));
        setPrice(request.price || 0);
        setTranslationStatus(normalizeTranslationType(request.translationStatus));
        setPaymentStatus(request.paymentStatus || 'Pending');
        setAdminNotes(request.adminNotes || '');
        setTranslationFile(null);
        setMethodologyFile(null);
        setIsEditModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load translation request:', err);
      toast.error('Failed to load translation request');
    }
  };

  const buildUpdateData = async () => {
    let translationFileUrl = null;
    let methodologyFileUrl = null;

    if (translationFile) {
      translationFileUrl = await uploadFileAndGetUrl(translationFile);
    }
    if (methodologyFile) {
      methodologyFileUrl = await uploadFileAndGetUrl(methodologyFile);
    }

    const updateData = {
      status,
      price: parseFloat(price) || 0,
      translationStatus,
      paymentStatus,
      adminNotes
    };

    if (translationFileUrl) {
      updateData.translationFile = {
        url: translationFileUrl,
        fileName: translationFile.name,
        fileSize: translationFile.size,
        uploadedAt: new Date().toISOString()
      };
    }

    if (methodologyFileUrl) {
      updateData.methodologyFile = {
        url: methodologyFileUrl,
        fileName: methodologyFile.name,
        fileSize: methodologyFile.size,
        uploadedAt: new Date().toISOString()
      };
    }

    return updateData;
  };

  const persistChanges = async () => {
    if (!selectedRequest) return;
    const updateData = await buildUpdateData();
    await updateTranslationRequest(selectedRequest._id, updateData);
    setTranslationFile(null);
    setMethodologyFile(null);
  };

  const handleSave = async () => {
    if (!selectedRequest) return;

    try {
      setSaving(true);
      await persistChanges();
      toast.success('Translation request updated successfully');
      setIsEditModalOpen(false);
      setSelectedRequest(null);
      await loadTranslationRequests();
    } catch (err) {
      console.error('Error updating translation request:', err);
      toast.error('Failed to update translation request');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!selectedRequest) return;

    try {
      setSaving(true);
      await persistChanges();

      const result = await apiFetch(`/api/admin/translation-requests/${selectedRequest._id}/create-invoice`, {
        method: 'POST'
      });

      if (result && result.success !== false) {
        toast.success('Invoice created successfully');
        await loadTranslationRequests();
        setIsEditModalOpen(false);

        if (result.data?.invoice?._id) {
          navigate(`/invoices/${result.data.invoice._id}`);
        }
      } else {
        throw new Error(result?.message || 'Failed to create invoice');
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      toast.error(err.message || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleRowAction = async (action, row) => {
    const id = typeof row === "string" ? row : row?.id || row?._id;
    
    if (action === "view-detail" || action === "edit") {
      await handleEdit(id);
    }
  };

  const columns = [
    { key: 'refNumber', accessor: 'refNumber', label: 'Reference Number' },
    { key: 'status', accessor: 'status', label: 'Request Status' },
    { key: 'translationStatus', accessor: 'translationStatus', label: 'Translation Type' },
    { key: 'paymentStatus', accessor: 'paymentStatus', label: 'Payment Status' },
    { key: 'price', accessor: 'price', label: 'Price' },
    { key: 'submittedAt', accessor: 'submittedAt', label: 'Submitted At' },
  ];

  const actionMenuItems = isEditable ? [
    { label: 'Edit', action: 'edit' },
  ] : [
    { label: 'View', action: 'view-detail' },
  ];

  const renderCell = (columnAccessor, row, column) => {
    // Use column.key if accessor is not provided
    const key = columnAccessor || column?.key;
    
    switch (key) {
      case 'refNumber':
        return row.refNumber || 'N/A';
      case 'status': {
        const displayStatus = normalizeTranslationRequestStatus(row.status);
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${translationRequestStatusBadgeClass(row.status)}`}>
            {displayStatus}
          </span>
        );
      }
      case 'translationStatus': {
        const displayType = normalizeTranslationType(row.translationStatus);
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${translationTypeBadgeClass(row.translationStatus)}`}>
            {displayType}
          </span>
        );
      }
      case 'paymentStatus':
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            row.paymentStatus === 'Paid' ? 'bg-green-100 text-green-800' :
            row.paymentStatus === 'NotApplicable' ? 'bg-slate-100 text-slate-700' :
            row.paymentStatus === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
            row.paymentStatus === 'Overdue' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {row.paymentStatus === 'NotApplicable' ? 'Not Applicable' : (row.paymentStatus || 'N/A')}
          </span>
        );
      case 'price':
        return row.price ? `£${parseFloat(row.price).toFixed(2)}` : 'N/A';
      case 'submittedAt':
        return row.submittedAt 
          ? new Date(row.submittedAt).toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric'
            })
          : 'N/A';
      default:
        return row[key] || 'N/A';
    }
  };

  const transformedRequests = translationRequests.map(req => ({
    ...req,
    id: req._id,
    status: normalizeTranslationRequestStatus(req.status || 'Under Review'),
    translationStatus: normalizeTranslationType(req.translationStatus),
    paymentStatus: req.paymentStatus || 'Pending',
  }));

  const canSendInvoice = selectedRequest && !selectedRequest.invoiceId;

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading translation requests...</div>
      ) : translationRequests.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <p>No translation requests found for this license.</p>
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={transformedRequests}
            currentPage={1}
            onPageChange={() => {}}
            rowsPerPage={10}
            pagination={{ total: transformedRequests.length, page: 1, limit: 10 }}
            showPagination={false}
            actionMenuItems={actionMenuItems}
            onRowAction={handleRowAction}
            renderCell={renderCell}
          />
        </>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">Edit Translation Request</h2>
                <button
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedRequest(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Request Info - all API data */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Request Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 block">Reference</span>
                      <p className="text-gray-900 font-medium">{selectedRequest.refNumber || '—'}</p>
                    </div>
                    {/* <div>
                      <span className="text-gray-600 block">Status</span>
                      <p className="text-gray-900">{selectedRequest.status || '—'}</p>
                    </div> */}
                    <div className="col-span-2">
                      <span className="text-gray-600 block">Custom translation </span>
                      <p className="text-gray-900">{selectedRequest.customTranslationsText || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600 block">Selected languages</span>
                      <p className="text-gray-900">
                        {selectedRequest.selectedLanguages?.length > 0
                          ? selectedRequest.selectedLanguages.map(l => `${l.country} - ${l.language}`).join(', ')
                          : 'None'}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600 block">Additional requirement</span>
                      <p className="text-gray-900">{selectedRequest.additionalRequirement || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 block">Is urgent</span>
                      <p className="text-gray-900">{selectedRequest.isUrgent ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 block">Territory</span>
                      <p className="text-gray-900">{selectedRequest.territory || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Request Status (editable) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Request Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {TRANSLATION_REQUEST_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Translation Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Translation Type
                  </label>
                  <select
                    value={translationStatus}
                    onChange={(e) => setTranslationStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {TRANSLATION_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Payment Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Overdue">Overdue</option>
                    <option value="NotApplicable">Not Applicable</option>
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (£)
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    onWheel={(e) => e.target.blur()}

                  />

                </div>

                {/* Translation File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Translation File
                  </label>
                  {selectedRequest.translationFile?.url ? (
                    <div className="mb-2">
                      <a
                        href={selectedRequest.translationFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 underline"
                      >
                        {selectedRequest.translationFile.fileName || 'View Translation File'}
                      </a>
                    </div>
                  ) : null}
                  <input
                    type="file"
                    onChange={(e) => setTranslationFile(e.target.files[0])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    accept=".pdf,.doc,.docx"
                  />
                  {translationFile && (
                    <p className="text-sm text-gray-600 mt-2">Selected: {translationFile.name}</p>
                  )}
                </div>

                {/* Methodology File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Methodology File
                  </label>
                  {selectedRequest.methodologyFile?.url ? (
                    <div className="mb-2">
                      <a
                        href={selectedRequest.methodologyFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 underline"
                      >
                        {selectedRequest.methodologyFile.fileName || 'View Methodology File'}
                      </a>
                    </div>
                  ) : null}
                  <input
                    type="file"
                    onChange={(e) => setMethodologyFile(e.target.files[0])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    accept=".pdf,.doc,.docx"
                  />
                  {methodologyFile && (
                    <p className="text-sm text-gray-600 mt-2">Selected: {methodologyFile.name}</p>
                  )}
                </div>

                {/* Admin Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Notes
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Add notes about this translation request..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4 border-t">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  
                  {canSendInvoice && (
                    <button
                      onClick={handleSendInvoice}
                      disabled={saving}
                      className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Processing...' : 'Send Invoice'}
                    </button>
                  )}

                  {selectedRequest.invoiceId && (
                    <button
                      onClick={() => navigate(`/invoices/${selectedRequest.invoiceId}`)}
                      className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      View Invoice
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationRequestsTab;
